import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Box, Button, Chip, IconButton, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CustomAvatar from '@ui/components/common/Avatar';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { HFilledTextField } from '@ui/components/common/TextField';
import { PageLayout } from '@ui/components/PageLayout';
import { centeredRowStack, colCenter } from '@ui/style/style';
import { AddCircle, UserOctagon, Eye } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { toast } from 'react-toastify';
import AddEditCollaborator from './AddEditCollaborator/AddEditCollaborator';
import AddEditCollaboratorDialog from './AddEditCollaborator/AddEditCollaboratorDialog';
import useCollaboratorsStyles from './useCollaboratorsStyles';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { Delete } from 'react-iconly';
import { createCollaboratorAsync, updateCollaboratorAsync, getAllCollaboratorsAsync, getCollaboratorByIdAsync, deleteCollaboratorAsync, disableCollaboratorAsync, enableCollaboratorAsync } from '@application/features/collaborator/collaboratorThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { CollaboratorRole } from '@application/enums/collaborator-role';
import { APIStatus } from '@application/enums/api-status';
import { StyledMenu } from '@ui/components/common/Menu';
import { useSelector } from 'react-redux';
import { CollaboratorDocumentType } from '@application/enums/collaborator-document-type';
import { hasPermission } from '@application/helpers/permission-helper';
const Collaborators = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useCollaboratorsStyles();
    const dispatch = useAppDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [editCollaborator, setEditCollaborator] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCollaboratorId, setSelectedCollaboratorId] = useState(null);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [openAddEditCollaboratorDialog, setOpenAddEditCollaboratorDialog] = useState(false);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({}); // Temporary filters while typing in dialog
    const [searchValue, setSearchValue] = useState('');
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [switchTargetRow, setSwitchTargetRow] = useState(null);
    const { collaborators, collaboratorsTotalCount, status, collaborator } = useAppSelector((state) => state.collaborator);
    const { selectedCompanyId } = useSelector((state) => state.company);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canReadCollaborators = hasPermission(loggedInUserPermissions, 'Permissions.Collaborator.Read');
    const canCreateCollaborators = hasPermission(loggedInUserPermissions, 'Permissions.Collaborator.Create');
    const canUpdateCollaborators = hasPermission(loggedInUserPermissions, 'Permissions.Collaborator.Update');
    const canDeleteCollaborators = hasPermission(loggedInUserPermissions, 'Permissions.Collaborator.Delete');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [anchorMoreOptionEl, setAnchorMoreOptionEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState();
    const handleMoreClick = (event, row) => {
        event.stopPropagation();
        setAnchorMoreOptionEl(event.currentTarget);
        setSelectedRow(row);
    };
    const handleCloseMenu = () => {
        setAnchorMoreOptionEl(null);
    };
    const moreMenuOptions = [
        {
            text: t('tasks.edit') || 'Edit',
            icon: _jsx(Icon, { icon: 'eva:edit-outline', fontSize: 20 }),
            disabled: !canUpdateCollaborators,
            to: () => {
                if (canUpdateCollaborators) {
                    handleEditClick(selectedRow?.id);
                    handleCloseMenu();
                }
            }
        },
        {
            text: t('tasks.delete') || 'Delete',
            icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
            color: theme.palette.error.light,
            disabled: !canDeleteCollaborators || Boolean(selectedRow?.isNDASigned),
            to: () => {
                if (canDeleteCollaborators && !selectedRow?.isNDASigned) {
                    handleDeleteClick(selectedRow?.id);
                    handleCloseMenu();
                }
            }
        }
    ];
    const GetAllCollaborators = () => {
        const request = {
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue || undefined,
            firstName: filters.firstName || undefined,
            lastName: filters.lastName || undefined,
            email: filters.email || undefined,
            phoneNo: filters.contact || filters.phoneNo || undefined,
            accessLevel: filters.accessLevel !== undefined ? filters.accessLevel : undefined,
            category: filters.category || undefined,
            description: filters.description || undefined,
            isEnabled: filters.active !== undefined && filters.active !== null ? filters.active : undefined
        };
        dispatch(getAllCollaboratorsAsync(request));
    };
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            GetAllCollaborators();
        }
    }, [
        dispatch,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        filters,
        selectedCompanyId
    ]);
    const handleToggleRoleActive = async (data) => {
        if (data.id && data.active) {
            await dispatch(disableCollaboratorAsync(data.id))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('collaborators.collaboratorsDeactivateConfirmed'));
                    GetAllCollaborators();
                }
                else {
                    toast.error(response.message || t('collaborators.failedToUpdateCollaborator'));
                }
            })
                .catch((error) => {
                const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                    ? error.message
                    : undefined;
                toast.error(errorMessage || t('collaborators.failedToUpdateCollaborator'));
            });
        }
        else if (data.id && !data.active) {
            await dispatch(enableCollaboratorAsync(data.id))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('collaborators.collaboratorsActivateConfirmed'));
                    GetAllCollaborators();
                }
                else {
                    toast.error(response.message || t('collaborators.failedToUpdateCollaborator'));
                }
            })
                .catch((error) => {
                const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                    ? error.message
                    : undefined;
                toast.error(errorMessage || t('collaborators.failedToUpdateCollaborator'));
            });
        }
    };
    useEffect(() => {
        if (editCollaborator && selectedCollaboratorId) {
            dispatch(getCollaboratorByIdAsync(selectedCollaboratorId));
        }
    }, [dispatch, editCollaborator, selectedCollaboratorId]);
    const getAccessLevelLabel = (accessLevel) => {
        switch (accessLevel) {
            case CollaboratorAccessLevel.All:
                return t('collaborators.accessLevelAll') || 'All';
            case CollaboratorAccessLevel.External:
                return t('collaborators.accessLevelExternal') || 'External';
            default:
                return '-';
        }
    };
    const getRoleLabel = (role) => {
        switch (role) {
            case CollaboratorRole.TransportManager:
                return t('collaborators.roleTransportManager') || 'Transport Manager';
            case CollaboratorRole.Collaborator:
            default:
                return t('collaborators.roleCollaborator') || 'Collaborator';
        }
    };
    const rows = useMemo(() => {
        if (!collaborators || !Array.isArray(collaborators)) {
            return [];
        }
        // Get current language code (e.g., 'en', 'nl', 'de')
        const currentLanguageCode = i18n.language?.toLowerCase() || 'en';
        return collaborators.map((collaborator) => {
            let ndaDocument;
            if (collaborator.isNDASigned && collaborator.signedNDALanguageId) {
                // If NDA is signed, show only the signed document (ignore app language change)
                ndaDocument = collaborator.collaboratorDocuments?.find((doc) => doc.type === CollaboratorDocumentType.NonDisclosureAgreement &&
                    doc.languageId === collaborator.signedNDALanguageId);
            }
            else {
                // If NDA is not signed, find document matching current app language
                ndaDocument = collaborator.collaboratorDocuments?.find((doc) => doc.type === CollaboratorDocumentType.NonDisclosureAgreement &&
                    doc.language?.code?.toLowerCase() === currentLanguageCode);
                // Fallback: if no document found for current language, try to find any NDA document
                if (!ndaDocument) {
                    ndaDocument = collaborator.collaboratorDocuments?.find((doc) => doc.type === CollaboratorDocumentType.NonDisclosureAgreement);
                }
            }
            return {
                id: collaborator.id,
                firstName: collaborator.firstName,
                lastName: collaborator.lastName,
                email: collaborator.email,
                contact: `${collaborator.email}\n${collaborator.phoneNo || ''}`,
                userProfilePic: collaborator.profilePictureUrl || '',
                accessLevel: getAccessLevelLabel(collaborator.accessLevel),
                role: getRoleLabel(collaborator.role ?? CollaboratorRole.Collaborator),
                category: collaborator.category || '-',
                active: collaborator.isEnabled,
                nda: ndaDocument?.name || '-',
                ndaUrl: ndaDocument?.url || null,
                isNDASigned: collaborator.isNDASigned
            };
        });
    }, [collaborators, t, i18n.language]);
    const collaboratorColumns = [
        {
            field: 'userProfilePic',
            headerName: '',
            minWidth: 50,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                const src = params.value;
                const firstName = params.row.firstName;
                const lastName = params.row.lastName;
                const fullName = `${firstName || ''} ${lastName || ''}`.trim();
                return (_jsx(Box, { width: '100%', height: '100%', onClick: (event) => event.stopPropagation(), sx: colCenter, children: _jsx(CustomAvatar, { src: src, name: fullName }) }));
            }
        },
        {
            field: 'firstName',
            headerName: t('labels.firstName'),
            flex: 1,
            minWidth: 130,
            type: 'string',
            sortable: false
        },
        {
            field: 'lastName',
            headerName: t('labels.lastName'),
            flex: 1,
            minWidth: 130,
            sortable: false,
            type: 'string'
        },
        {
            field: 'email',
            sortable: false,
            headerName: t('labels.email'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                return (_jsx(Box, { flex: 1, minWidth: 0, children: _jsx(Typography, { variant: 'body2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', children: params.value }) }));
            }
        },
        {
            field: 'contact',
            headerName: t('labels.contact'),
            flex: 1,
            align: 'left',
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                const [email, phoneNo] = (params.value || '').split('\n');
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        height: '100%'
                    }, children: phoneNo && (_jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.primary }, children: `+${phoneNo}` })) }));
            }
        },
        {
            field: 'role',
            headerName: t('collaborators.role') || 'Role',
            flex: 1,
            align: 'left',
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: _jsx(Typography, { variant: 'body2', children: params.value || '-' }) }));
            }
        },
        {
            field: 'accessLevel',
            headerName: t('labels.accessLevel'),
            flex: 1,
            align: 'left',
            minWidth: 135,
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: _jsx(Typography, { variant: 'body2', children: params.value || '-' }) }));
            }
        },
        {
            field: 'category',
            headerName: t('labels.category'),
            flex: 1,
            align: 'left',
            minWidth: 110,
            sortable: false,
            renderCell: (params) => {
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: _jsx(Typography, { variant: 'body2', children: params.value || '-' }) }));
            }
        },
        {
            field: 'nda',
            headerName: t('collaborators.nda') || 'NDA',
            flex: 0.5,
            align: 'left',
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                const ndaName = params.value;
                const ndaUrl = params.row.ndaUrl;
                if (ndaName === '-' || !ndaUrl) {
                    return (_jsx(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            height: '100%'
                        }, children: _jsx(Typography, { variant: 'body2', sx: { color: theme.palette.text.secondary }, children: "-" }) }));
                }
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%',
                        width: '100%',
                        gap: 1
                    }, children: _jsx(Tooltip, { title: t('actions.view') || 'View', arrow: true, placement: 'top', children: _jsx(IconButton, { size: 'medium', color: 'inherit', onClick: (e) => {
                                e.stopPropagation();
                                window.open(ndaUrl, '_blank');
                            }, children: _jsx(Eye, { variant: 'Outline' }) }) }) }));
            }
        },
        {
            field: 'isNDASigned',
            headerName: t('collaborators.status') || 'Status',
            flex: 1,
            align: 'left',
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const isSigned = params.value;
                const statusText = isSigned
                    ? t('collaborators.signed')
                    : t('collaborators.signaturePending');
                const statusColor = isSigned ? theme.palette.success.main : theme.palette.warning.main;
                return (_jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: _jsx(Chip, { label: statusText, size: 'small', color: 'default', sx: {
                            fontWeight: 500,
                            borderRadius: '8px'
                        } }) }));
            }
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 120,
            sortable: false,
            renderCell: (params) => (_jsx(Box, { onClick: (event) => event.stopPropagation(), children: _jsx(Switch, { size: 'medium', checked: params.row.active, disabled: params.row.id === selectedCollaboratorId || !canUpdateCollaborators, onChange: () => {
                        setSwitchTargetRow(params.row);
                        setSwitchDialogOpen(true);
                    } }) }))
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                // Check if user has any permissions to perform actions on this row
                const hasActions = canUpdateCollaborators || canDeleteCollaborators;
                return (_jsx(Tooltip, { title: t('actions.more') || 'More', arrow: true, children: _jsx(IconButton, { size: 'medium', color: 'inherit', disabled: !hasActions, onClick: (e) => handleMoreClick(e, params.row), children: _jsx(Icon, { icon: 'eva:more-horizontal-fill' }) }) }));
            }
        }
    ];
    const handleEditClick = (collaboratorId) => {
        if (!canUpdateCollaborators) {
            toast.error(t('common.unauthorized') || 'You do not have permission to edit collaborators');
            return;
        }
        if (!collaboratorId)
            return;
        setSelectedCollaboratorId(collaboratorId);
        setEditCollaborator(true);
        if (isSmallScreen) {
            setDrawerOpen(true);
        }
        else {
            setOpenAddEditCollaboratorDialog(true);
        }
    };
    const handleDeleteClick = (collaboratorId) => {
        if (!canDeleteCollaborators) {
            toast.error(t('common.unauthorized') || 'You do not have permission to delete collaborators');
            return;
        }
        if (!collaboratorId)
            return;
        setSelectedCollaboratorId(collaboratorId);
        setOpenDeleteConfirmation(true);
    };
    const handleDelete = () => {
        if (!selectedCollaboratorId)
            return;
        dispatch(deleteCollaboratorAsync(selectedCollaboratorId))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message || t('collaborators.collaboratorRemoved'));
                GetAllCollaborators();
            }
            else {
                toast.error(response.message || t('collaborators.failedToRemoveCollaborator'));
            }
        })
            .catch((error) => {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('collaborators.failedToRemoveCollaborator'));
        });
        setOpenDeleteConfirmation(false);
        setSelectedCollaboratorId(null);
    };
    const handleCollaboratorSubmit = async (data) => {
        try {
            if (editCollaborator && 'id' in data) {
                // Update existing collaborator
                await dispatch(updateCollaboratorAsync(data)).unwrap();
                toast.success(t('collaborators.collaboratorUpdated') || 'Collaborator updated successfully');
            }
            else {
                // Create new collaborator
                await dispatch(createCollaboratorAsync(data)).unwrap();
                toast.success(t('collaborators.collaboratorAdded') || 'Collaborator added successfully');
            }
            if (isSmallScreen) {
                setDrawerOpen(false);
            }
            else {
                setOpenAddEditCollaboratorDialog(false);
            }
            setEditCollaborator(false);
            setSelectedCollaboratorId(null);
            // Refresh the collaborators list
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                firstName: filters.firstName || undefined,
                lastName: filters.lastName || undefined,
                email: filters.email || undefined,
                phoneNo: filters.contact || filters.phoneNo || undefined,
                accessLevel: filters.accessLevel !== undefined ? filters.accessLevel : undefined,
                category: filters.category || undefined,
                description: filters.description || undefined,
                isEnabled: filters.active !== undefined && filters.active !== null ? filters.active : undefined
            };
            dispatch(getAllCollaboratorsAsync(request));
        }
        catch (error) {
            // Extract error message from ApiError (matches pattern used in Login page)
            let errorMessage = t('collaborators.error') || 'An error occurred';
            if (error && typeof error === 'object') {
                // Check if it's an ApiError with error property (primary check)
                if ('error' in error && typeof error.error === 'string') {
                    errorMessage = error.error;
                }
                else if ('message' in error && typeof error.message === 'string') {
                    // Fallback to message property if error property doesn't exist
                    errorMessage = error.message;
                }
            }
            toast.error(errorMessage);
        }
    };
    const renderFilterInput = (column) => {
        const handleChange = (value) => {
            setTempFilters((prev) => ({
                ...prev,
                [column.field]: value
            }));
        };
        if (column.field === 'accessLevel') {
            // Special handling for accessLevel dropdown
            const accessLevelOptions = [
                { label: t('collaborators.accessLevelAll'), value: CollaboratorAccessLevel.All },
                { label: t('collaborators.accessLevelExternal'), value: CollaboratorAccessLevel.External }
            ];
            const selectedOption = accessLevelOptions.find((opt) => opt.value === tempFilters[column.field]) || null;
            return (_jsx(Autocomplete, { options: accessLevelOptions, value: selectedOption, onChange: (_, newValue) => {
                    handleChange(newValue?.value ?? null);
                }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, renderInput: (params) => (_jsx(HFilledTextField, { ...params, label: column.headerName, fullWidth: true, bg: 'grey8' })), fullWidth: true }));
        }
        if (column.field === 'active') {
            // Special handling for active dropdown
            const activeOptions = [
                { label: 'Yes', value: true },
                { label: 'No', value: false }
            ];
            const filterValue = tempFilters[column.field];
            const selectedOption = filterValue !== undefined && filterValue !== null
                ? activeOptions.find((opt) => opt.value === filterValue) || null
                : null;
            return (_jsx(Autocomplete, { options: activeOptions, value: selectedOption, onChange: (_, newValue) => {
                    handleChange(newValue?.value ?? null);
                }, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, renderInput: (params) => (_jsx(HFilledTextField, { ...params, label: column.headerName, fullWidth: true, bg: 'grey8' })), fullWidth: true }));
        }
        if (column.type === 'string' || !column.type) {
            return (_jsx(HFilledTextField, { type: 'string', bg: 'grey8', label: column.headerName, fullWidth: true, value: tempFilters[column.field] ?? '', onChange: (e) => handleChange(e.target.value) }));
        }
        return null;
    };
    const renderAllColumnFilters = () => {
        return (_jsx(Box, { display: 'flex', flexDirection: 'column', gap: 2, padding: 2, children: collaboratorColumns
                .filter((column) => column.field !== 'userProfilePic' && column.field !== 'actions')
                .map((column) => (_jsx(Box, { children: renderFilterInput(column) }, column.field))) }));
    };
    return (_jsxs(PageLayout, { title: t('common.collaborators') || 'Collaborators', secondTitle: t('collaborators.createFindAndManageCollaboratorsHere') ||
            'Create, find, and manage collaborators here', showLeftIcon: true, leftIcon: _jsx(UserOctagon, { variant: 'Outline' }), rightContent: _jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: isSmallScreen ? 'medium' : 'large', fullWidth: isSmallScreen, color: 'primary', disabled: !canCreateCollaborators, onClick: () => {
                if (canCreateCollaborators) {
                    if (isSmallScreen) {
                        setEditCollaborator(false);
                        setSelectedCollaboratorId(null);
                        setDrawerOpen(true);
                    }
                    else {
                        setOpenAddEditCollaboratorDialog(true);
                    }
                }
            }, children: t('collaborators.addCollaborator') || 'Add Collaborator' }), children: [isSmallScreen ? (_jsx(_Fragment, { children: _jsx(BDrawer, { anchor: 'bottom', title: editCollaborator
                        ? t('collaborators.editCollaborator') || t('users.editUser')
                        : t('collaborators.addCollaborator'), open: drawerOpen, onClose: () => {
                        setEditCollaborator(false);
                        setDrawerOpen(false);
                        setSelectedCollaboratorId(null);
                    }, children: _jsx(AddEditCollaborator, { edit: editCollaborator, collaborator: collaborator || null, onSubmit: handleCollaboratorSubmit, onCancel: () => {
                            setDrawerOpen(false);
                            setEditCollaborator(false);
                            setSelectedCollaboratorId(null);
                        } }) }) })) : (_jsx(Box, { children: openAddEditCollaboratorDialog && (_jsx(AddEditCollaboratorDialog, { open: openAddEditCollaboratorDialog, edit: editCollaborator, collaborator: collaborator || null, onClose: () => {
                        setOpenAddEditCollaboratorDialog(false);
                        setEditCollaborator(false);
                        setSelectedCollaboratorId(null);
                    }, onSubmitCollaborator: handleCollaboratorSubmit })) })), _jsxs(Box, { sx: { ...styles.collaboratorContainer }, children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', children: _jsx(SearchFilter, { searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('collaborators.searchCollaborators') || 'Search collaborators...', onFilterClick: () => {
                                setTempFilters(filters);
                                setOpenFilterDialog(true);
                            }, filterLabel: t('events.filters') || 'Filters', breakpoint: 'lg', onClear: () => {
                                setSearchValue('');
                                setFilters({});
                                setTempFilters({});
                            }, showFilterClearButton: Object.keys(filters).length > 0 ? true : false, handleFilterClear: () => {
                                setFilters({});
                                setTempFilters({});
                            } }) }), _jsx(PrimaryGrid, { rows: rows, haveSearch: false, showFilters: false, columns: collaboratorColumns, maxHeight: 'calc(100vh - 160px)', paginationMode: 'server', noRowsText: t('dataGrid.noRowsAdd', {
                            entityPlural: (t('common.collaborators') || 'collaborators').toLowerCase(),
                            entitySingular: (t('collaborators.collaborator') || 'collaborator').toLowerCase()
                        }), paginationModel: paginationModel, rowCount: collaboratorsTotalCount || 0, loading: status === APIStatus.PENDING, onPaginationModelChange: setPaginationModel, disableRowSelectionOnClick: true, showHeaderControls: false })] }), openDeleteConfirmation &&
                (isSmallScreen ? (_jsx(BDrawer, { open: openDeleteConfirmation, onClose: () => {
                        setOpenDeleteConfirmation(false);
                        setSelectedCollaboratorId(null);
                    }, title: t('common.deleteConfirmation'), dialogActions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => {
                                    setOpenDeleteConfirmation(false);
                                    setSelectedCollaboratorId(null);
                                }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDelete, sx: {
                                    backgroundColor: theme.palette.error.main
                                }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })) : (_jsx(CustomDialog, { open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => {
                        setOpenDeleteConfirmation(false);
                        setSelectedCollaboratorId(null);
                    }, actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => {
                                        setOpenDeleteConfirmation(false);
                                        setSelectedCollaboratorId(null);
                                    }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleDelete, sx: {
                                        backgroundColor: theme.palette.error.main
                                    }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) }))), isSmallScreen ? (_jsxs(BDrawer, { anchor: 'bottom', title: t('users.filters') || 'Filters', filterLabel: t('actions.clearFilters'), open: openFilterDialog, onClose: () => {
                    setTempFilters(filters); // Reset temp filters to current filters on close
                    setOpenFilterDialog(false);
                }, showClearFilterButton: Object.keys(filters).length > 0 ? true : false, handleClearFilter: () => {
                    setFilters({});
                    setTempFilters({});
                    setOpenFilterDialog(false);
                }, children: [renderAllColumnFilters(), _jsxs(Box, { sx: { ...centeredRowStack, p: 2, gap: 1 }, children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                    setTempFilters(filters); // Reset temp filters to current filters
                                    setOpenFilterDialog(false);
                                }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                    // Remove keys with null, undefined, or empty string values
                                    const cleanedFilters = Object.fromEntries(Object.entries(tempFilters).filter(([_, value]) => {
                                        return value !== null && value !== undefined && value !== '';
                                    }));
                                    setFilters(cleanedFilters); // Apply cleaned filters
                                    setOpenFilterDialog(false);
                                }, children: t('actions.applyFilters') })] })] })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: openFilterDialog, title: t('users.filters') || 'Filters', onClose: () => {
                    setTempFilters(filters); // Reset temp filters to current filters on close
                    setOpenFilterDialog(false);
                }, actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: () => {
                                setTempFilters(filters); // Reset temp filters to current filters
                                setOpenFilterDialog(false);
                            }, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                // Remove keys with null, undefined, or empty string values
                                const cleanedFilters = Object.fromEntries(Object.entries(tempFilters).filter(([_, value]) => {
                                    return value !== null && value !== undefined && value !== '';
                                }));
                                setFilters(cleanedFilters); // Apply cleaned filters
                                setOpenFilterDialog(false);
                            }, children: t('actions.applyFilters') })] }), children: renderAllColumnFilters() })), switchDialogOpen &&
                switchTargetRow &&
                (isSmallScreen ? (_jsx(BDrawer, { open: switchDialogOpen, onClose: () => setSwitchDialogOpen(false), title: switchTargetRow.active
                        ? t('collaborators.deactivateCollaborator')
                        : t('collaborators.activateCollaborator'), dialogActions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsxs(Button, { variant: 'contained', color: switchTargetRow.active ? 'error' : 'inherit', fullWidth: true, onClick: () => {
                                    handleToggleRoleActive(switchTargetRow);
                                    setSwitchDialogOpen(false);
                                }, sx: {
                                    backgroundColor: switchTargetRow.active ? theme.palette.error.main : undefined
                                }, children: [' ', switchTargetRow.active
                                        ? t('actions.deactivate')
                                        : t('actions.activate')] })] }), children: _jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: switchTargetRow.active
                                ? t('collaborators.collaboratorDeactivationConfirm')
                                : t('collaborators.collaboratorActivationConfirm') }) }) })) : (_jsx(CustomDialog, { open: switchDialogOpen, title: switchTargetRow.active
                        ? t('collaborators.deactivateCollaborator')
                        : t('collaborators.activateCollaborator'), onClose: () => setSwitchDialogOpen(false), actions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: switchTargetRow.active ? 'error' : 'inherit', fullWidth: true, onClick: () => {
                                    handleToggleRoleActive(switchTargetRow);
                                    setSwitchDialogOpen(false);
                                }, sx: {
                                    backgroundColor: switchTargetRow.active ? theme.palette.error.main : undefined
                                }, children: switchTargetRow.active
                                    ? t('actions.deactivate')
                                    : t('actions.activate') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: switchTargetRow.active
                                ? t('collaborators.collaboratorDeactivationConfirm')
                                : t('collaborators.collaboratorActivationConfirm') }) }) }))), anchorMoreOptionEl && (_jsx(StyledMenu, { anchorEl: anchorMoreOptionEl, open: Boolean(anchorMoreOptionEl), onClose: handleCloseMenu, actions: moreMenuOptions }))] }));
};
export default Collaborators;
